import {
  type ApolloError,
  type FetchResult,
  gql,
  useMutation,
} from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useTenant } from "@/providers/session";

import { GET_INTEGRATION_QUERY } from "./get";

type CreateIntegration = {
  externalId?: string;
  integrationName: string;
  integrationAuth?: any;
  integrationConfig?: any;
};

export const useCreateIntegration = (): [
  (
    input: CreateIntegration,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<FetchResult>,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [createIntegration, { data, loading, error }] = useMutation(
    CREATE_INTEGRATION_MUTATION,
    {
      refetchQueries: [GET_INTEGRATION_QUERY],
    },
  );

  const handleCreateIntegration = async (
    input: CreateIntegration,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    toast.dismiss();
    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await createIntegration({
        variables: {
          tenantId,
          externalId: input.externalId,
          integrationName: input.integrationName,
          integrationAuth: input.integrationAuth,
          integrationConfig: input.integrationConfig,
        },
      });

      if (response.errors) {
        throw new Error("error creating integration");
      }

      onSuccess();
      toast.success(t("generic_success"), { id: toastId });

      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId,
        richColors: true,
      });
      throw error;
    }
  };

  return [
    handleCreateIntegration,
    { data: data?.createIntegration, loading, error },
  ];
};

const CREATE_INTEGRATION_MUTATION = gql`
  mutation CreateIntegration(
    $tenantId: String
    $externalId: String
    $integrationName: String
    $integrationAuth: JSON
    $integrationConfig: JSON
  ) {
    createIntegration(
      tenantId: $tenantId
      externalId: $externalId
      integrationName: $integrationName
      integrationAuth: $integrationAuth
      integrationConfig: $integrationConfig
    ) {
      integrationId
      externalId
      integrationName
      integrationAuth
      integrationConfig
      tenantId
      createdAt
      updatedAt
    }
  }
`;
